import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Input, Tag, Form, Checkbox, Select, DatePicker, notification } from 'antd';
// import { useSelector } from 'react-redux';
import { EditOutlined, DeleteOutlined, ReloadOutlined, PlusCircleOutlined, FilterOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'underscore';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import 'dayjs/locale/lt';
import 'moment/locale/lt';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import { AppDispatch, CombinedStates } from '../../core/types';
import { Category, ExportIndicatorsPayload } from '../../core/indicator/types';
import Main from '../../components/layout/Main';
import { getIndicators, createIndicator, updateIndicator, deleteIndicator, bulkUpdateIndicatorsStatus, exportIndicators, recalculateComputedIndicatorAll } from '../../core/indicator/indicatorApi';
import { loadSingleIndicator, resetSingleIndicator } from '../../core/indicator/indicatorSlice';
import { schoolTypeOptions, defaultSchoolYearOptions, generateSchoolYearOptions } from '../../core/utils/helpers';

import './indicatorsListing.scss';

const frequencyOptions = {
    school_year: { label: 'Mokslo metai', color: 'geekblue' },
    yearly: { label: 'Kiekvienais metais', color: 'green' },
    quarterly: { label: 'Ketvirtis', color: 'orange' },
    monthly: { label: 'Kas mėnesį', color: 'purple' },
    // once: { label: 'Vienkartinis', color: 'red' },
    twice_a_year_july_january: { label: '2 kartus per metus (liepos ir sausio mėnesiai)', color: 'blue' },
    twice_a_year_may_october: { label: '2 kartus per metus (gegužės ir spalio mėnesiai)', color: 'blue' },
    semester: { label: 'Pusmetis', color: 'cyan' },
};

const unitOptions = [
    { value: 'vnt', label: 'Vienetai', color: 'blue' },
    { value: 'proc', label: 'Procentai', color: 'green' },
    { value: 'eur', label: 'Eurai', color: 'gold' },
    { value: 'eur_thousands', label: 'Tūkstančiai eurų', color: 'volcano' },
    { value: 'asm', label: 'Asmenys', color: 'cyan' },
    { value: 'ratio', label: 'Santykis', color: 'purple' },
    { value: 'taip_ne', label: 'Taip / Ne', color: 'lime' },
    { value: 'text', label: 'Tekstas', color: 'green' },
];

const statusOptions = [
    { value: 10, label: 'Aktyvus', color: 'green' },
    { value: 0, label: 'Neaktyvus', color: 'red' },
];

type TargetValueFormItem = { period?: string; target_value?: string; value?: string };

/** Inkaro metai tik UI (dayjs); į API siunčiama tik MM-DD. 2000 m. – keliamieji (02-29). */
const COLLECTION_ANCHOR_YEAR = 2000;

function isValidIndicatorMmDd(s: string): boolean {
    if (!/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(s)) return false;
    const [ms, ds] = s.split('-');
    if (!ms || !ds) return false;
    const m = parseInt(ms, 10);
    const d = parseInt(ds, 10);
    const t = new Date(COLLECTION_ANCHOR_YEAR, m - 1, d);
    return t.getFullYear() === COLLECTION_ANCHOR_YEAR && t.getMonth() === m - 1 && t.getDate() === d;
}

function mmDdToDayjs(value: string | null | undefined): Dayjs | null {
    if (!value || typeof value !== 'string' || !isValidIndicatorMmDd(value)) return null;
    const [mm, dd] = value.split('-');
    if (!mm || !dd) return null;
    return dayjs(`${COLLECTION_ANCHOR_YEAR}-${mm}-${dd}`);
}

function MmDdCollectionPicker(props: {
    value?: string | null;
    onChange?: (v: string | null) => void;
}) {
    const { value, onChange } = props;
    return (
        <DatePicker
            allowClear
            style={{ width: '100%' }}
            format="MM-DD"
            placeholder="MM-DD"
            value={mmDdToDayjs(value) ?? undefined}
            onChange={(d) => (onChange ? onChange(d ? d.format('MM-DD') : null) : undefined)}
            defaultPickerValue={dayjs(`${COLLECTION_ANCHOR_YEAR}-06-15`)} />
    );
}

MmDdCollectionPicker.defaultProps = {
    value: null,
    onChange: undefined,
};

const optionalMmDdRule = {
    validator(_: unknown, value: string | null | undefined) {
        if (value === undefined || value === null || value === '') {
            return Promise.resolve();
        }
        if (typeof value !== 'string' || !isValidIndicatorMmDd(value)) {
            return Promise.reject(new Error('Netinkama data: naudokite formatą MM-DD (pvz. 09-01)'));
        }
        return Promise.resolve();
    },
};

/** MM-DD intervalas su galimu metų persidengimu (pabaiga anksčiau už pradžią tame pačiame kalendoriniame cikle). */
function isCollectionPeriodLongerThanMonths(
    start: string | null | undefined,
    end: string | null | undefined,
    maxMonths: number,
): boolean {
    if (!start || !end || !isValidIndicatorMmDd(start) || !isValidIndicatorMmDd(end)) return false;
    const d0 = mmDdToDayjs(start);
    const d1 = mmDdToDayjs(end);
    if (!d0 || !d1 || d0.isSame(d1, 'day')) return false;
    let endAdj = d1;
    if (endAdj.isBefore(d0)) {
        endAdj = endAdj.add(1, 'year');
    }
    return endAdj.diff(d0, 'month', true) > maxMonths;
}

const IndicatorsListing = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [searchTerm, setSearchTerm] = useState('');

    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [dependsOn, setDependsOn] = useState<number[]>([]);
    const [isComputed, setIsComputed] = useState(false);
    const [isRecalculatingAll, setIsRecalculatingAll] = useState(false);
    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [isBulkStatusModalVisible, setIsBulkStatusModalVisible] = useState<boolean>(false);
    const [bulkStatusForm] = Form.useForm();
    const [assignedScope, setAssignedScope] = useState<'all' | 'types' | 'schools' | 'education_center' | 'education_department'>('all');
    const [filters, setFilters] = useState({
        category: [] as number[],
        unit: [] as string[],
        input_frequency: [] as string[],
        is_computed: [] as boolean[],
        assigned_scope: [] as string[],
        status: [] as number[],
    });
    const schoolYearOptions = useMemo(() => generateSchoolYearOptions(undefined, 10) || defaultSchoolYearOptions, []);
    const [form] = Form.useForm();

    const {
        indicators,
        isFetching,
        isFetchingIndicatorsExport,
        singleIndicator,
        categories,
    } = useSelector((state:CombinedStates) => state.indicator);

    const { userData } = useSelector((state:CombinedStates) => state.user);

    const {
        schools,
    } = useSelector((state:CombinedStates) => state.school);

    const handleScopeChange = (value: 'all' | 'types' | 'schools' | 'education_center' | 'education_department') => {
        setAssignedScope(value);
        form.setFieldsValue({ assigned_scope: value });
    };

    const handleCreate = () => {
        form
            .validateFields()
            .then(values => {
                // Additional validation for types scope
                if (values.assigned_scope === 'types') {
                    const hasSchoolTypes = values.school_types && values.school_types.length > 0;
                    const hasSchoolIds = values.school_ids && values.school_ids.length > 0;

                    if (!hasSchoolTypes && !hasSchoolIds) {
                        form.setFields([
                            {
                                name: 'school_types',
                                errors: ['Pasirinkite bent vieną mokyklos tipą arba mokyklą'],
                            },
                            {
                                name: 'school_ids',
                                errors: ['Pasirinkite bent vieną mokyklos tipą arba mokyklą'],
                            },
                        ]);
                        return;
                    }
                }

                const rawTargetValues = (values.target_values as TargetValueFormItem[]) || [];
                const preparedTargetValues = rawTargetValues
                    .filter((item) => item?.period && (item.target_value || item.value))
                    .map((item) => ({
                        period: item.period as string,
                        target_value: (item.target_value || item.value) as string,
                    }));

                const periodUsage: Record<string, number> = preparedTargetValues.reduce((acc, item) => {
                    acc[item.period] = (acc[item.period] || 0) + 1;
                    return acc;
                }, {} as Record<string, number>);

                const duplicates = preparedTargetValues
                    .map((item, idx) => ({ ...item, idx }))
                    .filter(({ period }) => (periodUsage[period] || 0) > 1);

                if (duplicates.length > 0) {
                    form.setFields(duplicates.map(({ idx }) => ({
                        name: ['target_values', idx, 'period'],
                        errors: ['Pasirinkite skirtingus mokslo metus'],
                    })));
                    return;
                }

                const payload = {
                    ...values,
                    target_values: preparedTargetValues,
                    collection_start: values.collection_start || null,
                    collection_end: values.collection_end || null,
                };

                if (isCollectionPeriodLongerThanMonths(payload.collection_start, payload.collection_end, 10)) {
                    notification.warning({
                        message: 'Ilgas rinkimo periodas',
                        description: 'Pasirinktas duomenų rinkimo periodas ilgesnis nei 10 mėnesių.',
                        placement: 'topRight',
                    });
                }

                if (singleIndicator?.id) {
                    dispatch(updateIndicator({
                        id: singleIndicator.id,
                        ...payload,
                    }));
                } else {
                    dispatch(createIndicator(payload));
                }
                setIsModalVisible(false);
                form.resetFields();
                dispatch(resetSingleIndicator());
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleIndicator(id));
        setIsModalVisible(true);
    };

    useEffect(() => {
        if (singleIndicator && isModalVisible) {
            const depends = (singleIndicator.depends_on || []).map((v: string | number) => parseInt(v.toString(), 10));
            const categoryIds = (singleIndicator.categories || []).map((cat: Category) => cat.id);

            // Set assigned scope
            const scope = singleIndicator.assigned_scope || 'all';
            setAssignedScope(scope as 'all' | 'types' | 'schools');

            type SchoolTypeValue = string | { type: string };
            type AllowedSchoolValue = number | { id: number };
            const schoolTypes = (singleIndicator.school_types as SchoolTypeValue[] || []).map((item) => (typeof item === 'string' ? item : item.type));
            const schoolIds = (singleIndicator.allowed_schools as AllowedSchoolValue[] || []).map((school) => (typeof school === 'number' ? school : school.id));
            const targetValues = (singleIndicator as { target_values?: TargetValueFormItem[] }).target_values?.map((item) => ({
                period: item.period || item.value,
                target_value: item.target_value || item.value || '',
            })) || [];

            form.setFieldsValue({
                ...singleIndicator,
                depends_on: depends,
                categories: categoryIds,
                assigned_scope: scope,
                school_types: schoolTypes,
                school_ids: schoolIds,
                target_values: targetValues.length ? targetValues : [],
                status: singleIndicator.status !== undefined ? singleIndicator.status : 10,
            });
            setDependsOn(depends);
            setIsComputed(!!singleIndicator.is_computed);
        }
    }, [singleIndicator, isModalVisible, form, schoolYearOptions]);

    const handleComputedChange = (e: CheckboxChangeEvent) => {
        setIsComputed(e.target.checked);
    };

    const validateFormula = (_rule: unknown, inputValue: string): Promise<void> => {
        if (!inputValue) return Promise.resolve();

        const value = inputValue.replace(/,/g, '.');

        const allowedCharsRegex = /^[R0-9+\-*/().\s]*$/;
        if (!allowedCharsRegex.test(value)) {
            return Promise.reject(
                new Error('Leidžiami tik R{id}, skaičiai ir matematiniai simboliai: +, -, *, /, (, ).'),
            );
        }

        const usedVars = value.match(/R\d+/g) || [];
        const allowedIds = dependsOn.map((id) => `R${id}`);
        const invalidVars = usedVars.filter((v) => !allowedIds.includes(v));

        if (invalidVars.length > 0) {
            return Promise.reject(
                new Error(`Formulėje naudoti rodikliai, kurie nepažymėti kaip priklausomi: ${invalidVars.join(', ')}`),
            );
        }

        return Promise.resolve();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        dispatch(resetSingleIndicator());
    };

    const handleRecalculateAll = async () => {
        if (!singleIndicator?.id || isRecalculatingAll) return;

        setIsRecalculatingAll(true);
        try {
            const result = await dispatch(recalculateComputedIndicatorAll({
                indicator_id: singleIndicator.id,
            })).unwrap();

            const counts = [
                result.scope === 'school' ? `Mokyklos: ${result.schools}` : null,
                `Sukurta: ${result.created}`,
                `Atnaujinta: ${result.updated}`,
                `Nepasikeitė: ${result.unchanged}`,
                `Nepavyko: ${result.failed}`,
            ].filter(Boolean).join(', ');

            notification.success({
                message: result.message || 'Rodiklio reikšmės perskaičiuotos.',
                description: counts,
            });
        } catch {
            // Klaidos pranešimą rodo API kvietimas
        } finally {
            setIsRecalculatingAll(false);
        }
    };

    // Trigger fetch on component mount
    useEffect(() => {
        if (isEmpty(indicators)) {
            dispatch(getIndicators({}));
        }
    }, []);

    // Local search and filter function
    const getFilteredIndicators = () => {
        let filteredIndicators = [...indicators];

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filteredIndicators = filteredIndicators.filter((indicator) => (
                indicator.id.toString().includes(searchLower)
                || indicator.title?.toLowerCase().includes(searchLower)
                || indicator.description?.toLowerCase().includes(searchLower)
                || indicator.unit?.toLowerCase().includes(searchLower)
                || indicator.unit_description?.toLowerCase().includes(searchLower)
            ));
        }

        // Apply category filter
        if (filters.category.length > 0) {
            filteredIndicators = filteredIndicators.filter((indicator) => {
                // Use pivot.category_id for filtering since that's the actual relationship ID
                type CategoryWithPivot = Category & { pivot?: { category_id?: number } };
                const indicatorCategoryIds = indicator.categories?.map((cat: CategoryWithPivot) => cat.pivot?.category_id ?? cat.id) || [];
                // Convert both arrays to strings for comparison to handle type mismatches
                const filterIds = filters.category.map(id => String(id));
                const indicatorIds = indicatorCategoryIds.map(id => String(id));
                return filterIds.some(categoryId => indicatorIds.includes(categoryId));
            });
        }

        // Apply unit filter
        if (filters.unit.length > 0) {
            filteredIndicators = filteredIndicators.filter((indicator) => (
                indicator.unit && filters.unit.includes(indicator.unit)
            ));
        }

        // Apply input frequency filter
        if (filters.input_frequency.length > 0) {
            filteredIndicators = filteredIndicators.filter((indicator) => (
                filters.input_frequency.includes(indicator.input_frequency)
            ));
        }

        // Apply computed type filter
        if (filters.is_computed.length > 0) {
            filteredIndicators = filteredIndicators.filter((indicator) => (
                filters.is_computed.includes(indicator.is_computed)
            ));
        }

        // Apply assigned scope filter
        if (filters.assigned_scope.length > 0) {
            filteredIndicators = filteredIndicators.filter((indicator) => (
                filters.assigned_scope.includes(indicator.assigned_scope)
            ));
        }

        // Apply status filter
        if (filters.status.length > 0) {
            filteredIndicators = filteredIndicators.filter((indicator) => {
                const indicatorStatus = indicator.status !== undefined ? indicator.status : 10; // Default to active if not set
                return filters.status.includes(indicatorStatus);
            });
        }

        return filteredIndicators;
    };

    const doSearch = (term: string) => {
        setSearchTerm(term);
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Ar tikrai norite ištrinti rodiklį?',
            okText: 'Taip, ištrinti',
            cancelText: 'Atšaukti',
            okType: 'danger',
            onOk() {
                dispatch(deleteIndicator({ id }));
            },
        });
    };

    const handleBulkStatusUpdate = () => {
        bulkStatusForm
            .validateFields()
            .then(values => {
                const indicatorIds = selectedRowKeys.map(key => Number(key));
                dispatch(bulkUpdateIndicatorsStatus({
                    indicator_ids: indicatorIds,
                    status: values.status,
                }))
                    .then(() => {
                        setIsBulkStatusModalVisible(false);
                        setSelectedRowKeys([]);
                        bulkStatusForm.resetFields();
                        dispatch(getIndicators({}));
                    });
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const canExportIndicators = userData?.role_id === 1 || userData?.role_id === 2;

    const buildExportPayload = () => {
        const payload: ExportIndicatorsPayload = {};

        const trimmed = searchTerm.trim();
        if (trimmed) payload.search = trimmed;

        if (filters.unit.length > 0) payload.unit = filters.unit;
        if (filters.input_frequency.length > 0) payload.input_frequency = filters.input_frequency;
        if (filters.is_computed.length > 0) payload.is_computed = filters.is_computed;
        if (filters.category.length > 0) payload.category = filters.category;

        // API expects a single assigned_scope string (optional)
        if (filters.assigned_scope.length === 1) {
            const [scope] = filters.assigned_scope;
            if (scope) {
                payload.assigned_scope = scope as NonNullable<ExportIndicatorsPayload['assigned_scope']>;
            }
        }

        return payload;
    };

    const getFilenameFromDisposition = (disposition?: string) => {
        if (!disposition) return null;
        const match = /filename\*?=(?:UTF-8''|")?([^;"\n]+)"?/i.exec(disposition);
        if (!match?.[1]) return null;
        try {
            return decodeURIComponent(match[1]);
        } catch (e) {
            return match[1];
        }
    };

    const handleExportIndicators = async () => {
        try {
            const payload = buildExportPayload();
            const response = await dispatch(exportIndicators(payload)).unwrap();

            const contentDisposition = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
            const serverFilename = getFilenameFromDisposition(contentDisposition);
            const fallbackName = `indikatoriai_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;
            const filename = serverFilename || fallbackName;

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // notification is handled in thunk; keep silent here
            console.error('Export indicators failed:', error);
        }
    };

    return (
        <>
            <Main
                className="data-listing"
                title={<div>Rodiklių administravimas</div>}
                headerTitle={<div>Rodiklių administravimas</div>}
                menuWidth={350}
                menuPlacement="left"
                toolbar={[]}
                showFooter={false}
                floatingHeader={false}
                footerContent={<div />}
                floatingFooter
                backLink={false}
                breadcrumb={null}>
                <Card style={{ marginTop: '20px' }}>
                    <Row className="listing-table-title" justify="space-between" align="middle">
                        <Col className="listing-table-title-left">
                            <h3 style={{ marginBottom: 0 }}>Rodikliai</h3>
                            <span style={{ fontWeight: 500 }}>
                                Iš viso: {getFilteredIndicators().length}
                                {(searchTerm || Object.values(filters).some(filter => filter.length > 0)) && ` (filtruota iš ${indicators.length})`}
                            </span>
                        </Col>
                        <Col>
                            <Space>
                                <Button icon={<FilterOutlined />} onClick={() => setIsFilterVisible(true)}>
                                    Filtras
                                </Button>
                                <Input
                                    placeholder="Paieška..."
                                    value={searchTerm}
                                    onChange={(e) => doSearch(e.target.value)} />
                                <Tooltip title="Atnaujinti sąrašą">
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => {
                                            dispatch(getIndicators({}));
                                            setSearchTerm('');
                                            setFilters({
                                                category: [],
                                                unit: [],
                                                input_frequency: [],
                                                is_computed: [],
                                                assigned_scope: [],
                                                status: [],
                                            });
                                        }} />
                                </Tooltip>
                                {canExportIndicators && (
                                    <Button
                                        loading={isFetchingIndicatorsExport}
                                        icon={<DownloadOutlined />}
                                        onClick={handleExportIndicators}>
                                        Eksportuoti į Excel
                                    </Button>
                                )}
                                <Button
                                    loading={isFetching}
                                    icon={<PlusCircleOutlined />}
                                    type="primary"
                                    onClick={() => {
                                        dispatch(resetSingleIndicator());
                                        form.setFieldsValue({ status: 10 });
                                        setIsModalVisible(true);
                                    }}>
                                    Pridėti naują rodiklį
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                    {selectedRowKeys.length > 0 && (
                        <Row style={{ marginBottom: 16, padding: '12px', background: '#f0f0f0', borderRadius: '4px' }}>
                            <Col span={24}>
                                <Space>
                                    <span>Pasirinkta: {selectedRowKeys.length} rodiklių</span>
                                    <Button
                                        type="primary"
                                        onClick={() => setIsBulkStatusModalVisible(true)}>
                                        Keisti būseną
                                    </Button>
                                    <Button onClick={() => setSelectedRowKeys([])}>
                                        Atšaukti pasirinkimą
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    )}
                    <Table
                        dataSource={getFilteredIndicators()}
                        loading={isFetching}
                        scroll={{ x: 'max-content' }}
                        rowKey="id"
                        rowSelection={{
                            selectedRowKeys,
                            onChange: (selectedKeys) => {
                                setSelectedRowKeys(selectedKeys);
                            },
                            getCheckboxProps: () => ({
                                name: 'indicator',
                            }),
                        }}
                        columns={[
                            {
                                title: 'Id',
                                dataIndex: 'id',
                                key: 'id',
                            },
                            {
                                title: 'Pavadinimas',
                                dataIndex: 'title',
                                key: 'title',
                                width: 250,
                                render: (text: string) => (
                                    <Tooltip title={text}>
                                        <div style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 250,
                                        }}>
                                            {text}
                                        </div>
                                    </Tooltip>
                                ),
                            },
                            {
                                title: 'Vienetas',
                                dataIndex: 'unit',
                                key: 'unit',
                                render: (value: string) => {
                                    const unit = unitOptions.find((opt) => opt.value === value);
                                    return unit ? <Tag color={unit.color}>{unit.label}</Tag> : value;
                                },
                            },
                            {
                                title: 'Duomenys renkami',
                                dataIndex: 'input_frequency',
                                key: 'input_frequency',
                                render: (value: string) => {
                                    const option = frequencyOptions[value as keyof typeof frequencyOptions];
                                    return option ? <Tag color={option.color}>{option.label}</Tag> : value;
                                },
                            },
                            {
                                title: 'Kategorijos',
                                dataIndex: 'categories',
                                key: 'categories',
                                render: (currentCategories: Category[]) => (
                                    <span>{currentCategories?.map(cat => <Tag key={cat.id}>{cat.title}</Tag>)}</span>
                                ),
                            },
                            {
                                title: 'Tipas',
                                dataIndex: 'is_computed',
                                key: 'is_computed',
                                render: (value: boolean) => (value ? <Tag color="purple">Skaičiuojamas</Tag> : <Tag color="blue">Įvedamas</Tag>),
                            },
                            {
                                title: 'Kam taikomas',
                                dataIndex: 'assigned_scope',
                                key: 'assigned_scope',
                                render: (value: string) => {
                                    const scopeLabels = {
                                        all: 'Visos mokyklos',
                                        types: 'Pagal tipą',
                                        schools: 'Konkrečios mokyklos',
                                        education_center: 'Švietimo centras',
                                        education_department: 'Švietimo skyrius',
                                    };
                                    const getColor = (scope: string) => {
                                        switch (scope) {
                                        case 'all': return 'green';
                                        case 'types': return 'blue';
                                        case 'schools': return 'orange';
                                        case 'education_center': return 'purple';
                                        default: return 'red';
                                        }
                                    };
                                    return <Tag color={getColor(value)}>{scopeLabels[value as keyof typeof scopeLabels] || value}</Tag>;
                                },
                            },
                            {
                                title: 'Būsena',
                                dataIndex: 'status',
                                key: 'status',
                                render: (value: number) => {
                                    const status = statusOptions.find((opt) => opt.value === (value !== undefined ? value : 10));
                                    return status ? <Tag color={status.color}>{status.label}</Tag> : <Tag>{value}</Tag>;
                                },
                            },
                            {
                                title: 'Redaguota',
                                dataIndex: 'updated_at',
                                key: 'updated_at',
                                render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: 'Veiksmai',
                                key: 'actions',
                                render: (_text, record) => (
                                    <Space>
                                        <Button icon={<EditOutlined />} onClick={() => handleEdit(record.id)}>Redaguoti</Button>
                                        <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)}>Pašalinti</Button>
                                    </Space>
                                ),
                            },
                        ]} />
                </Card>
            </Main>
            <Modal
                title={singleIndicator?.id ? 'Atnaujinti rodiklį' : 'Sukurti naują rodiklį'}
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                className="indicator-modal"
                width="90%"
                footer={(
                    <Space>
                        {isComputed && singleIndicator?.id && (
                            <Tooltip title="Atnaujinkite reikšmes, jeigu keitėsi formulė ir nauja formulė jau buvo išsaugota.">
                                <Button
                                    icon={<ReloadOutlined />}
                                    loading={isRecalculatingAll}
                                    onClick={handleRecalculateAll}>
                                    Perskaičiuoti reikšmes
                                </Button>
                            </Tooltip>
                        )}
                        <Button onClick={handleCancel}>
                            Atšaukti
                        </Button>
                        <Button type="primary" onClick={handleCreate}>
                            {singleIndicator?.id ? 'Atnaujinti' : 'Sukurti'}
                        </Button>
                    </Space>
                )}>
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Pavadinimas"
                        name="title"
                        rules={[
                            { required: true, message: 'Įveskite pavadinimą' },
                            { max: 191, message: 'Pavadinimas negali viršyti 191 simbolių' },
                        ]}>
                        <Input.TextArea rows={2} maxLength={191} showCount />
                    </Form.Item>

                    <Form.Item label="Aprašymas" name="description">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item
                        label="Iš kokio dokumento paimtas rodiklis"
                        name="source_document"
                        rules={[{ max: 1000, message: 'Per ilgas tekstas' }]}>
                        <Input.TextArea rows={2} placeholder="Pvz.: Rodiklių rinkinio 2025 m. 1.3 punktas" />
                    </Form.Item>

                    <Form.Item
                        label="Duomenų šaltinis"
                        name="data_source"
                        rules={[{ max: 255, message: 'Per ilgas tekstas' }]}>
                        <Input placeholder="Pvz.: Nacionalinė švietimo agentūra, savivaldybės ataskaitos" />
                    </Form.Item>

                    <Form.List name="target_values">
                        {(fields, { add, remove }) => (
                            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                                <div style={{ fontWeight: 600 }}>Siekimo reikšmės pagal mokslo metus</div>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row key={key} gutter={16} align="middle">
                                        <Col span={10}>
                                            <Form.Item
                                                {...restField}
                                                label={name === 0 ? 'Mokslo metai' : undefined}
                                                name={[name, 'period']}
                                                rules={[{ required: true, message: 'Pasirinkite mokslo metus' }]}>
                                                <Select
                                                    placeholder="Pasirinkite mokslo metus"
                                                    options={schoolYearOptions} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={10}>
                                            <Form.Item
                                                {...restField}
                                                label={name === 0 ? 'Tikslinė reikšmė' : undefined}
                                                name={[name, 'target_value']}
                                                rules={[{ required: true, message: 'Įveskite reikšmę' }, { max: 255, message: 'Per ilgas tekstas' }]}>
                                                <Input placeholder="Pvz.: ≥ 90%" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={4} style={{ marginTop: name === 0 ? 22 : 0 }}>
                                            <Button danger type="link" onClick={() => remove(name)}>
                                                Pašalinti
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Button type="dashed" onClick={() => add({ period: schoolYearOptions[0]?.value })} block>
                                    Pridėti siekiamą reikšmę
                                </Button>
                            </Space>
                        )}
                    </Form.List>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Matavimo vienetas" name="unit" rules={[{ required: true, message: 'Pasirinkite matavimo vienetą' }]}>
                                <Select allowClear placeholder="Pasirinkite vienetą">
                                    {unitOptions.map(opt => (
                                        <Select.Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Matavimo vieneto aprašymas"
                                name="unit_description"
                                rules={[{ max: 50, message: 'Per ilgas tekstas (maks. 50 simbolių)' }]}>
                                <Input placeholder="Pvz.: Procentai, Vienetai, Tūkstančiai eurų..." />
                            </Form.Item>

                        </Col>
                    </Row>

                    <Form.Item
                        label="Duomenų suvedimo dažnumas"
                        name="input_frequency"
                        rules={[{ required: true, message: 'Pasirinkite dažnumą' }]}>
                        <Select placeholder="Pasirinkite dažnumą">
                            {Object.entries(frequencyOptions).map(([value, option]) => (
                                <Select.Option key={value} value={value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div style={{ marginBottom: 8, color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                        Kasmet nuo … iki … — kada turi būti suvesti duomenys (mėnuo ir diena, be metų). Neprivaloma.
                    </div>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Duomenų rinkimo laikotarpio pradžia"
                                name="collection_start"
                                rules={[optionalMmDdRule]}>
                                <MmDdCollectionPicker />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Duomenų rinkimo laikotarpio pabaiga"
                                name="collection_end"
                                rules={[optionalMmDdRule]}>
                                <MmDdCollectionPicker />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Kategorijos"
                        name="categories"
                        rules={[{ required: true, message: 'Pasirinkite bent vieną kategoriją' }]}>
                        <Select
                            mode="multiple"
                            showSearch
                            filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder="Pasirinkite kategorijas">
                            {categories?.map((cat) => (
                                <Select.Option key={cat.id} value={cat.id}>
                                    {cat.title}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Kam taikomas rodiklis"
                        name="assigned_scope"
                        rules={[{ required: true, message: 'Pasirinkite, kas pildo rodiklį' }]}>
                        <Select placeholder="Pasirinkite" onChange={handleScopeChange}>
                            <Select.Option value="all">Visos mokyklos</Select.Option>
                            <Select.Option value="types">Pagal mokyklos tipą arba konkrečią mokyklą</Select.Option>
                            <Select.Option value="education_center">Tik švietimo centrui</Select.Option>
                            <Select.Option value="education_department">Tik švietimo skyriui</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Būsena"
                        name="status"
                        initialValue={10}
                        rules={[{ required: true, message: 'Pasirinkite statusą' }]}>
                        <Select placeholder="Pasirinkite statusą">
                            {statusOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {assignedScope === 'types' && (
                        <Form.Item
                            label="Mokyklų tipai"
                            name="school_types">
                            <Select
                                mode="multiple"
                                allowClear
                                options={schoolTypeOptions}
                                placeholder="Pasirinkite tipus" />
                        </Form.Item>
                    )}

                    {assignedScope === 'types' && (
                        <Form.Item
                            label="Mokyklos"
                            name="school_ids">
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                placeholder="Pasirinkite mokyklas">
                                {schools.map((school) => (
                                    <Select.Option key={school.id} value={school.id}>
                                        {school.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {/*
                        <Form.Item name="is_required" valuePropName="checked">
                            <Checkbox>Rodiklis privalomas</Checkbox>
                        </Form.Item>
                    */}

                    <Form.Item name="is_computed" valuePropName="checked">
                        <Checkbox onChange={handleComputedChange}>Skaičiuojamas rodiklis</Checkbox>
                    </Form.Item>
                    <Form.Item name="is_critical" valuePropName="checked">
                        <Checkbox>Kritinis rodiklis <small style={{ color: '#888' }}>(Šį rodiklį gali pildyti tik švietimo skyrius arba švietimo centras.)</small></Checkbox>
                    </Form.Item>
                    {(assignedScope === 'education_center' || assignedScope === 'education_department') && (
                        <Form.Item name="is_school_allowed" valuePropName="checked">
                            <Checkbox>Ar mokykla gali įtraukti šį rodiklį į savo mokyklos rodiklių palyginimą? <small style={{ color: '#888' }}>( Mokykla galėtų įtraukti į rodiklių palyginimą ir duomenys butų prieinami visoms mokykloms. )</small></Checkbox>
                        </Form.Item>
                    )}

                    {isComputed && (
                        <>
                            <Form.Item
                                label="Formulė"
                                name="formula"
                                rules={[{ required: true, message: 'Įveskite formulę' }, { validator: validateFormula }]}
                                validateTrigger={['onChange', 'onBlur']}
                                extra="Naudokite R{id}, kur {id} yra kito rodiklio ID. Pvz.: R12 / R15 * 100">
                                <Input.TextArea rows={3}
                                    placeholder="Pvz.: R12 / R15 * 100"
                                    onChange={(e) => {
                                        const originalValue = e.target.value;
                                        const normalizedValue = originalValue.replace(/r(\d+)/g, 'R$1');
                                        form.setFieldsValue({ formula: normalizedValue });
                                    }} />
                            </Form.Item>

                            <Form.Item
                                label="Priklausantys rodikliai"
                                name="depends_on"
                                rules={[{ required: true, message: 'Pasirinkite bent vieną rodiklį' }]}
                                extra="Būtina pasirinkti visus rodiklius, kurie naudojami formulėje (pvz. R1, R2)">
                                <Select
                                    mode="multiple"
                                    showSearch
                                    value={dependsOn}
                                    placeholder="Pasirinkite priklausomus rodiklius"
                                    filterOption={(input, option) => {
                                        const q = input.trim().toLowerCase();
                                        if (!q) return true;
                                        const id = Number(option?.value);
                                        const indicator = indicators.find((i) => i.id === id);
                                        if (!indicator) return false;
                                        const title = indicator.title.toLowerCase();
                                        const idStr = String(indicator.id);
                                        return (
                                            title.includes(q)
                                            || idStr.includes(q)
                                            || `r${idStr}`.includes(q)
                                        );
                                    }}
                                    onChange={(values) => {
                                        setDependsOn(values);
                                        form.setFieldsValue({ depends_on: values }); // sync form state
                                    }}>
                                    {indicators.map((indicator) => (
                                        <Select.Option key={indicator.id} value={indicator.id}>
                                            {indicator.title} (R{indicator.id})
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
            <Modal
                title="Filtruoti rodiklius"
                visible={isFilterVisible}
                onCancel={() => setIsFilterVisible(false)}
                onOk={() => {
                    setIsFilterVisible(false);
                }}
                okText="Uždaryti"
                cancelText="Atšaukti"
                footer={[
                    <Button key="clear"
                        onClick={() => {
                            setFilters({
                                category: [],
                                unit: [],
                                input_frequency: [],
                                is_computed: [],
                                assigned_scope: [],
                                status: [],
                            });
                            setIsFilterVisible(false);
                        }}>
                        Išvalyti filtrus
                    </Button>,
                    <Button key="close"
                        type="primary"
                        onClick={() => setIsFilterVisible(false)}>
                        Uždaryti
                    </Button>,
                ]}>
                <Form layout="vertical">
                    <Form.Item label="Kategorijos">
                        <Select
                            mode="multiple"
                            showSearch
                            allowClear
                            filterOption={(input, option) => (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            value={filters.category}
                            onChange={(value) => setFilters({ ...filters, category: value })}>
                            {categories.map(cat => (
                                <Select.Option key={cat.id} value={cat.id}>{cat.title}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Vienetai">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.unit}
                            onChange={(value) => setFilters({ ...filters, unit: value })}>
                            {unitOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Duomenų dažnumas">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.input_frequency}
                            onChange={(value) => setFilters({ ...filters, input_frequency: value })}>
                            {Object.entries(frequencyOptions).map(([key, opt]) => (
                                <Select.Option key={key} value={key}>{opt.label}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Tipas">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.is_computed}
                            onChange={(value) => setFilters({ ...filters, is_computed: value })}>
                            <Select.Option value>Skaičiuojamas</Select.Option>
                            <Select.Option value={false}>Įvedamas</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Kam taikomas">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.assigned_scope}
                            onChange={(value) => setFilters({ ...filters, assigned_scope: value })}>
                            <Select.Option value="all">Visos mokyklos</Select.Option>
                            <Select.Option value="types">Pagal mokyklos tipą</Select.Option>
                            <Select.Option value="schools">Konkrečios mokyklos</Select.Option>
                            <Select.Option value="education_center">Tik švietimo centrui</Select.Option>
                            <Select.Option value="education_department">Tik švietimo skyriui</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Būsena">
                        <Select
                            mode="multiple"
                            allowClear
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value })}>
                            {statusOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Keisti rodiklių būseną"
                visible={isBulkStatusModalVisible}
                onOk={handleBulkStatusUpdate}
                onCancel={() => {
                    setIsBulkStatusModalVisible(false);
                    bulkStatusForm.resetFields();
                }}
                okText="Atnaujinti"
                cancelText="Atšaukti">
                <Form layout="vertical" form={bulkStatusForm}>
                    <Form.Item
                        label={`Pasirinkite būseną (${selectedRowKeys.length} rodiklių)`}
                        name="status"
                        rules={[{ required: true, message: 'Pasirinkite būseną' }]}>
                        <Select placeholder="Pasirinkite būseną">
                            {statusOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default IndicatorsListing;

IndicatorsListing.propTypes = {};
